import { LIVE_NPC_CONFIG, LIVE_NPC_SLOT_IDS, type LiveNpcSnapshot, type LiveNpcSlotId } from "@endclock/shared";
import { NpcMotionInterpolator } from "./npc-motion-interpolator.js";
import { CharacterAnimator } from "../character/character-animation.js";
import { loadCharacterGlb, type CharacterAsset } from "../character/character-asset.js";
import { CharacterRenderer } from "../character/character-renderer.js";
import type { AtmosphereFeatureFlags } from "../engine/atmosphere.js";
import type { Camera } from "../engine/camera.js";
import type { ActorContactShadows } from "../engine/actor-contact-shadows.js";
import { writeUprightTransform } from "../engine/math.js";
import { raySphereDistance, screenRay, terrainOccludesRay } from "../engine/picking.js";
import type { Renderer } from "../engine/renderer.js";
import type { NetworkClient } from "../game/network.js";
import { projectNameplate } from "../nameplates/nameplates.js";
import { NpcConversationPanel, npcAvailabilityLabel } from "./conversation-panel.js";

interface NpcActor {
  value: LiveNpcSnapshot;
  readonly motion: NpcMotionInterpolator;
  readonly transform: Float32Array;
  readonly label: HTMLElement;
  readonly name: HTMLElement;
  readonly button: HTMLButtonElement;
  readonly caption: HTMLElement;
  animator?: CharacterAnimator;
}

export class NpcWorld {
  readonly panel: NpcConversationPanel;
  private readonly layer = document.createElement("div");
  private readonly actors = new Map<LiveNpcSlotId, NpcActor>();
  private readonly assets = new Map<number, { asset: CharacterAsset; renderer: CharacterRenderer }>();
  private player = { x: 0, y: 0, z: 0 };
  private loadError: string | null = null;
  private disposed = false;
  private visibleCount = 0;
  private visibleTriangles = 0;

  constructor(private readonly renderer: Renderer, parent: HTMLElement, private readonly network: NetworkClient, flags: AtmosphereFeatureFlags) {
    this.layer.className = "npc-nameplates";
    parent.append(this.layer);
    this.panel = new NpcConversationPanel(parent, network);
    for (const [appearance] of LIVE_NPC_SLOT_IDS.entries()) {
      const id = `npc_${String(appearance + 1).padStart(2, "0")}`;
      void loadCharacterGlb(`/assets/characters/${id}.glb?v=immersive-1`).then((asset) => {
        if (!this.disposed) this.assets.set(appearance, { asset, renderer: new CharacterRenderer(renderer, asset, flags) });
      }).catch((error: unknown) => { this.loadError = error instanceof Error ? error.message : "NPC appearance unavailable"; });
    }
  }

  ingest(values: readonly LiveNpcSnapshot[], at: number, tick: number): void {
    const present = new Set<LiveNpcSlotId>();
    for (const value of values.slice(0, LIVE_NPC_CONFIG.slotCount)) {
      if (!LIVE_NPC_SLOT_IDS.includes(value.slotId)) continue;
      present.add(value.slotId);
      let actor = this.actors.get(value.slotId);
      if (!actor) {
        const label = document.createElement("div");
        label.className = "npc-nameplate";
        label.dataset.npcSlot = value.slotId;
        const name = document.createElement("strong");
        const button = document.createElement("button");
        button.type = "button";
        button.addEventListener("click", () => this.talk(value.slotId));
        const caption = document.createElement("span");
        caption.className = "npc-proactive-caption";
        caption.setAttribute("aria-live", "polite");
        label.append(name, button, caption);
        this.layer.append(label);
        actor = { value, motion: new NpcMotionInterpolator(value.x, value.y, value.z, value.yaw), transform: new Float32Array(16), label, name, button, caption };
        this.actors.set(value.slotId, actor);
      }
      if (actor.value.appearance !== value.appearance) delete actor.animator;
      actor.value = value;
      actor.motion.ingest(value.x, value.y, value.z, value.yaw, at, tick);
      actor.name.textContent = `${value.displayName} · NPC`;
      // Authoritative controls must stay current while GPU frames are delayed.
      this.refreshLabelContent(actor);
    }
    for (const [id, actor] of this.actors) if (!present.has(id)) { actor.label.remove(); this.actors.delete(id); }
  }

  update(delta: number, player: { x: number; y: number; z: number }): void {
    this.player = player;
    for (const actor of this.actors.values()) {
      this.refreshLabelContent(actor);
      actor.motion.update(delta);
      const loaded = this.assets.get(actor.value.appearance);
      if (!loaded) continue;
      actor.animator ??= new CharacterAnimator(loaded.asset);
      actor.animator.update(actor.motion.speed, false, delta);
    }
    this.panel.update();
  }

  render(camera: Camera, canvas: HTMLCanvasElement): void {
    this.layer.hidden = false;
    this.visibleCount = 0;
    this.visibleTriangles = 0;
    this.renderer.metrics.totalCharacters += this.actors.size;
    for (const actor of this.actors.values()) {
      const motion = actor.motion;
      const loaded = this.assets.get(actor.value.appearance);
      if (loaded && actor.animator && camera.isSphereVisible(motion.x, motion.y + 0.9, motion.z, 1.1)) {
        loaded.renderer.begin(camera.viewProjection, camera.position, camera.target);
        writeUprightTransform(actor.transform, 0, motion.x, motion.y, motion.z, motion.yaw, 1);
        loaded.renderer.draw(actor.transform, actor.animator.palette);
        this.renderer.metrics.visibleCharacters += 1;
        this.visibleCount += 1;
        this.visibleTriangles += loaded.renderer.triangleCount;
        this.renderer.metrics.characterJointUpdates += loaded.asset.joints.length;
      }
      const point = projectNameplate({ x: motion.x, y: motion.y + 2.1, z: motion.z }, this.player, camera.viewProjection, canvas.clientWidth, canvas.clientHeight);
      actor.label.hidden = !point;
      if (!point) continue;
      actor.label.style.transform = `translate(${point.x}px, ${point.y}px) translate(-50%, -100%)`;
      actor.label.style.opacity = String(point.opacity);
    }
  }

  pick(x: number, y: number, canvas: HTMLCanvasElement, camera: Camera, heightAt: (x: number, z: number) => number): boolean {
    const ray = screenRay(x, y, canvas.getBoundingClientRect(), camera.viewProjection);
    if (!ray) return false;
    let closest: { id: LiveNpcSlotId; distance: number } | null = null;
    for (const [id, actor] of this.actors) {
      const { x: npcX, y: npcY, z: npcZ } = actor.motion;
      const distance = raySphereDistance(ray, npcX, npcY + 0.9, npcZ, 0.9);
      if (distance !== null && (!closest || distance < closest.distance)) closest = { id, distance };
    }
    if (!closest || terrainOccludesRay(ray, closest.distance, heightAt)) return false;
    this.talk(closest.id);
    return true;
  }

  renderShadow(shadowCamera: Camera): void {
    for (const actor of this.actors.values()) {
      const loaded = this.assets.get(actor.value.appearance);
      const motion = actor.motion;
      if (!loaded || !actor.animator || !shadowCamera.isSphereVisible(motion.x, motion.y + 0.9, motion.z, 1.1)) continue;
      loaded.renderer.beginShadow(shadowCamera.viewProjection);
      writeUprightTransform(actor.transform, 0, motion.x, motion.y, motion.z, motion.yaw, 1);
      loaded.renderer.drawShadow(actor.transform, actor.animator.palette);
    }
  }

  addContactShadows(shadows: ActorContactShadows): void {
    for (const actor of this.actors.values()) {
      if (actor.animator && this.assets.has(actor.value.appearance)) shadows.addActor(actor.motion.x, actor.motion.y, actor.motion.z);
    }
  }

  hide(): void { this.layer.hidden = true; }
  reset(): void { for (const actor of this.actors.values()) actor.label.remove(); this.actors.clear(); this.panel.reset(); }
  snapshot() {
    return {
      loadedAppearances: this.assets.size, error: this.loadError, visibleCount: this.visibleCount, visibleTriangles: this.visibleTriangles,
      npcs: [...this.actors.values()].map(({ value, motion }) => ({ ...value, renderedX: motion.x, renderedY: motion.y, renderedZ: motion.z })),
      ...this.panel.snapshot(),
    };
  }
  dispose(): void { this.disposed = true; this.reset(); this.layer.remove(); this.panel.dispose(); }

  private refreshLabelContent(actor: NpcActor): void {
    const distance = Math.hypot(actor.value.x - this.player.x, actor.value.z - this.player.z);
    const text = npcAvailabilityLabel(actor.value, distance);
    if (actor.button.textContent !== text) actor.button.textContent = text;
    actor.button.disabled = actor.value.availability !== "AVAILABLE" || distance > LIVE_NPC_CONFIG.conversationRange;
    actor.label.dataset.availability = actor.value.availability;
    const caption = actor.value.caption;
    actor.caption.hidden = !caption || caption.expiresAt <= this.network.estimatedServerNow();
    const captionText = actor.caption.hidden ? "" : caption?.text ?? "";
    if (actor.caption.textContent !== captionText) actor.caption.textContent = captionText;
  }

  private talk(slotId: LiveNpcSlotId): void {
    const actor = this.actors.get(slotId);
    if (!actor || this.network.state !== "online") return;
    const distance = Math.hypot(actor.value.x - this.player.x, actor.value.z - this.player.z);
    if (actor.value.availability !== "AVAILABLE" || distance > LIVE_NPC_CONFIG.conversationRange) return;
    this.network.sendNpcCommand({ t: "npc_start", slotId });
  }
}
